import {useCallback, useMemo, useState} from "react";
import {defineMessage, FormattedMessage, MessageDescriptor, useIntl} from "react-intl";
import {FormattedPageTitle} from "@/components/pages";
import {useAuth} from "@/context/AuthProvider";
import AllocationTable from "./shared/AllocationTable";
import {Checkbox, Divider, FormControl, FormHelperText, Typography} from "@mui/material";
import {ServicesDataSource, useServicesDataSource} from "./servicesDataSource";
import Loading from "@/components/breadcrumbs/Loading";
import {ServicesDomainAllocationForm, ServicesDomainAllocationFormData} from "./form";
import {ErrorWithContext, FormActionButtons, MultipleLineError} from "@dvrt/graphql-widgets";
import {NewDomainAllocation} from "./dynamicDataSource";
import {Box} from "@mui/system";
import {SERVICE_NAME_MESSAGES} from "@/utils/translations";
import {isUnlimited} from "@/tools";

const GENERAL_TABLE_KEYS = ["fax", "group5", "group10", "group20"] as const;
const CC_TABLE_KEYS = ["ccAattendant", "ccQueue"] as const;

type Props = {
    domainId: number;
};

type ContentProps = {
    dataSource: ServicesDataSource;
} & Props;

const Content = ({domainId, dataSource}: ContentProps) => {
    const form = new ServicesDomainAllocationForm(domainId, dataSource.data);
    const [disabled, setDisabled] = useState(false);
    const auth = useAuth();
    const isSuperuser = auth.isSuperuser;

    async function submit() {
        setDisabled(true);
        if (await form.submit()) {
            const newData = await dataSource.refetch();
            form.newInitialData(ServicesDomainAllocationForm.createInitialData(newData));
        }
        setDisabled(false);
    }

    const ccEnabled = form.values.ccBase?.newDomainAllocated ?? false;

    const typedToDynamicAllocationMapper = useCallback(
        (key: keyof ServicesDomainAllocationFormData) => {
            // does not matter which field we use for this type because all of them are the same
            const allocation = form.values[key] as unknown as ServicesDomainAllocationFormData["ccAattendant"];

            let active = true;
            if (!ccEnabled && (key === "ccAattendant" || key === "ccQueue")) {
                active = false;
            }

            const mapped = {
                name: key,
                newAllocation: allocation.newDomainAllocated,
                domainAllocated: allocation.domainAllocated,
                domainUsed: allocation.domainUsed,
                systemLimit: allocation.systemLimit,
                totalAllocated: allocation.totalAllocated,
                included: allocation.included ?? 0,
                active,
            } as NewDomainAllocation;

            return mapped;
        },
        [ccEnabled, form.values],
    );

    const getAllocationsForTable = useCallback(
        (table: "general" | "cc") => {
            const tableKeys = table === "general" ? GENERAL_TABLE_KEYS : CC_TABLE_KEYS;
            return Object.keys(form.values)
                .filter((key) => tableKeys.includes(key as never))
                .map((key) => typedToDynamicAllocationMapper(key as keyof ServicesDomainAllocationFormData));
        },
        [form.values, typedToDynamicAllocationMapper],
    );

    const generalAllocations = getAllocationsForTable("general");
    const ccAllocations = getAllocationsForTable("cc");

    return (
        <div>
            <div style={{marginBottom: "20px"}}>
                <FormattedPageTitle
                    key={"title-services"}
                    desc={defineMessage({
                        id: "domain_settings.allocations.service_title",
                        defaultMessage: "Service allocations for {domain}",
                    })}
                    values={{domain: dataSource.domainName}}
                />
                <AllocationTable
                    label={defineMessage({id: "services", defaultMessage: "Services"})}
                    labelForAllocation={labelForAllocation}
                    allocations={generalAllocations}
                    errors={form.errorsWithContext}
                    update={(name, value) => form.updateAllocation(name as any, value)}
                    hasEditPermission={isSuperuser}
                    canSeeSystemAllocation={isSuperuser}
                />
                <Divider sx={{marginY: "24px"}} />
                <CCBaseCheckbox
                    allocation={form.values.ccBase}
                    onChange={(checked) => form.updateAllocation("ccBase", checked)}
                    isSuperuser={isSuperuser}
                    errors={form.errorsWithContext["ccBase"]}
                />
                <AllocationTable
                    label={defineMessage({id: "services", defaultMessage: "Services"})}
                    labelForAllocation={labelForAllocation}
                    allocations={ccAllocations}
                    errors={form.errorsWithContext}
                    update={(name, value) => form.updateAllocation(name as any, value)}
                    hasEditPermission={auth.hasPermission("edit_domain_licence")}
                    canSeeSystemAllocation={isSuperuser}
                    disabled={!ccEnabled}
                />
                <Divider />
                {isSuperuser && (
                    <div style={{width: "fit-content", margin: "0 auto", marginTop: "2rem"}}>
                        <FormActionButtons
                            key={"form-action-buttons-services"}
                            mainAction={submit}
                            secondaryAction={() => form.reset()}
                            mainButtonEnabled={form.hasChanged && !disabled}
                            secondaryButtonEnabled={form.hasChanged && !disabled}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

type CCBaseCheckboxProps = {
    allocation: ServicesDomainAllocationForm["values"]["ccBase"];
    onChange: (checked: boolean) => void;
    isSuperuser: boolean;
    errors?: ErrorWithContext[];
};

const CCBaseCheckbox = ({allocation, onChange, isSuperuser, errors}: CCBaseCheckboxProps) => {
    const intl = useIntl();

    const captionText = useMemo(() => {
        if (!isSuperuser) return "";

        let text = "";

        text += intl.formatMessage({id: "domain_settings.system_limit", defaultMessage: "System limit"});
        text += ": ";
        text += allocation.systemLimit ?? intl.formatMessage({id: "unlimited", defaultMessage: "Unlimited"});

        text += " | ";

        text += intl.formatMessage({id: "domain_settings.total_allocated", defaultMessage: "Total allocated"});
        text += ": ";
        text += allocation.totalAllocated;

        return text;
    }, [allocation, isSuperuser, intl]);

    return (
        <Box>
            <Typography variant="h6">
                <FormattedMessage
                    id="domain_settings.allocations.cc_base_description"
                    defaultMessage="The allocations below are related to Call Center and are only available if enabled. When enabled, the domain will automatically get 1 free auto attendant that does not get included in total allocations calculation."
                />
            </Typography>
            <FormControl error={errors?.length > 0} sx={{marginY: "8px"}}>
                <Box sx={{display: "flex", alignItems: "center"}}>
                    <Typography>
                        <FormattedMessage id="enabled" defaultMessage="Enabled" />
                    </Typography>
                    <span>:</span>
                    <Checkbox
                        checked={allocation.newDomainAllocated}
                        onChange={(_, checked) => onChange(checked)}
                        disabled={
                            !isSuperuser ||
                            //always enabled if want to deallocate, but do not allocate over
                            (!allocation.domainAllocated &&
                                !isUnlimited(allocation.systemLimit) &&
                                allocation.totalAllocated >= allocation.systemLimit)
                        }
                    />
                </Box>
                <FormHelperText>
                    <MultipleLineError errors={errors} />
                </FormHelperText>
                {isSuperuser && (
                    <Typography variant="caption" sx={{fontStyle: "italic"}}>
                        {captionText}
                    </Typography>
                )}
            </FormControl>
        </Box>
    );
};

export const DomainAllocationServices = ({domainId}: Props) => {
    const dataSource = useServicesDataSource(domainId);

    if (dataSource.loading) return <Loading />;

    return <Content domainId={domainId} dataSource={dataSource} />;
};

function labelForAllocation(name: string): MessageDescriptor {
    if (name in SERVICE_NAME_MESSAGES) return SERVICE_NAME_MESSAGES[name as keyof typeof SERVICE_NAME_MESSAGES];
    else {
        console.warn(`Unknown service name: ${name}`);
        return defineMessage({id: "unknown_service", defaultMessage: "Unknown service: {name}", values: {name}});
    }
}
